import './styles.css';

import React from 'react';

import { UseAuthError } from '@search/auth/src/useAuth';
import { AuthInfo } from '@search/auth/src/AuthInfo';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import { useGqlContext } from '@search/gql-client/src';
import { useEmailsState } from '@search/classified-common/src/SaveSearch/components/SaveSearchModal/useEmailsState';
import { userEmailsQuery } from '@search/classified-common/src/SaveSearch/gql/UserEmailsQuery';
import {
    UserEmailsQuery$data as UserEmailsQueryResponse,
    UserEmailsQuery$variables as UserEmailsQueryVariables
} from '@search/classified-common/src/SaveSearch/gql/__generated__/UserEmailsQuery.graphql';

import { validateEmail } from '../../../../domain/emails/validateEmail';

import { useClassifiedOfferDataLayerPush } from '../../../../domain/offer/analytics/ClassifiedOfferGtmContext';
import { ConfirmEmailMutation$variables as ConfirmEmailMutationVariables } from '../../../../domain/emails/ConfirmEmailMutation/__generated__/ConfirmEmailMutation.graphql';
import { SubscribeToPriceForm } from './components/SubscribeToPriceForm';

function useUserEmailsFetcher() {
    const gql = useGqlContext();

    return React.useCallback(
        (vars: UserEmailsQueryVariables, opts?: { signal?: AbortSignal }) => gql.client<
            UserEmailsQueryVariables,
            UserEmailsQueryResponse
            >(userEmailsQuery, vars, opts).then(),
        [ gql ]
    );
}

type Props = {
    onClose: () => void;
    offerId: string;
    subscriptionPriceEmail: string;
    setSubscriptionPriceEmail: (value: string) => void;
    handleSubmit: (email: string) => void;
    confirmEmail: (props: ConfirmEmailMutationVariables) => void;
    hasSubscription?: boolean | null;
    formType: 'desktop-snippet' | 'mobile1' | 'mobile2' | 'desktop-card';
    nearPriceHistoryPlot?: boolean;
}

export const SubscribeToPriceChange = (props: Props) => {
    const {
        setSubscriptionPriceEmail,
        handleSubmit,
        subscriptionPriceEmail,
        confirmEmail,
        offerId,
        hasSubscription,
        formType,
        nearPriceHistoryPlot
    } = props;

    const {
        emailsSuggestions,
        selectedEmail,
        setEmail,
        setEmailOption,
        isNewEmail
    } = useEmailsState(subscriptionPriceEmail, setSubscriptionPriceEmail);

    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const { waitAuth } = useM2AuthContext();
    const auth = useM2AuthContext();

    const openOrAuth = (authInfo: AuthInfo) => {
        return new Promise((resolve, reject) => {
            if (authInfo.isAuthenticated) {
                resolve(null);
            } else {
                reject();
            }
        });
    };

    const onSubmit = React.useCallback(() => {
        handleSubmit(subscriptionPriceEmail);
    }, [ subscriptionPriceEmail, handleSubmit ]);

    const onConfirmEmail = React.useCallback(() => {
        confirmEmail({ email: subscriptionPriceEmail });
    }, [ confirmEmail, subscriptionPriceEmail ]);

    const isValidEmail = React.useMemo(() => {
        return validateEmail(subscriptionPriceEmail);
    }, [ subscriptionPriceEmail ]);

    const isDisabledButton = React.useMemo(() => {
        if (subscriptionPriceEmail) return ! isValidEmail;

        return ! subscriptionPriceEmail || false;
    }, [ isValidEmail, subscriptionPriceEmail ]);

    const userEmailInfo = useUserEmailsFetcher();

    const onSave = React.useCallback(() => {
        switch (formType) {
            // аналитика странно собирается
            case 'desktop-snippet':
                dataLayerPush({ event: 'classified_pr_subscr_snip_on' });
                break;
            case 'mobile1':
            case 'mobile2':
                dataLayerPush({ event: 'classified_pr_subscr_offer_on' });
                break;
            default:
                break;
        }
        if (! auth.auth.isAuthenticated) {
            waitAuth(authInfo => openOrAuth(authInfo).catch(() => {
                throw new UseAuthError();
            }), {
                modalTitle: 'Подписка на изменение цены',
                modalSubtitle: 'Чтобы подписаться на изменение цены, войдите или зарегистрируйтесь.',
                continueAfterLogin: true
            }).then(() => {
                userEmailInfo({}).then(data => {
                    if (subscriptionPriceEmail &&
                        ! data.data?.userEmails.emailsList?.includes(subscriptionPriceEmail)) {
                        onConfirmEmail();
                    } else {
                        onSubmit();
                    }
                });
            });
            return null;
        }
        if (auth.auth.isAuthenticated) {
            if (subscriptionPriceEmail && isNewEmail) {
                onConfirmEmail();
            } else {
                onSubmit();
            }
        }
    }, [ onSubmit, onConfirmEmail, subscriptionPriceEmail, isNewEmail ]);

    return (
        <SubscribeToPriceForm
            offerId={offerId}
            isDisabledButton={isDisabledButton}
            hasSubscription={hasSubscription}
            isValidEmail={isValidEmail}
            formType={formType}
            onSave={onSave}
            setEmail={setEmailOption}
            onEmailChange={setEmail}
            selectedEmail={selectedEmail}
            emailsSuggestions={emailsSuggestions}
            nearPriceHistoryPlot={nearPriceHistoryPlot}
        />
    );
};
